import React, { useState, useEffect } from 'react';
import SearchBox from '../components/SearchBox';
import SearchForm from '../components/SearchForm';
import SearchResults from '../components/SearchResults';
import LoadingSpinner from '../components/LoadingSpinner';
import { searchProjects, searchProjectsStructured } from '../utils/api';

function SearchPage() {
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [originalQuery, setOriginalQuery] = useState('');
  const [enhancedQuery, setEnhancedQuery] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [searchMode, setSearchMode] = useState('structured'); // 'text' or 'structured'

  useEffect(() => {
    let interval;
    if (searchLoading) {
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setLoadingTime(elapsed);
        
        if (elapsed < 10) {
          setLoadingMessage('🤖 AI is enhancing your query...');
        } else if (elapsed < 30) {
          setLoadingMessage('🔍 Searching through the vector database...');
        } else if (elapsed < 60) {
          setLoadingMessage('🧠 AI is analyzing and ranking results...');
        } else {
          setLoadingMessage('⏱️ This is taking longer than usual. Please wait...');
        }
      }, 1000);
    } else {
      setLoadingTime(0);
      setLoadingMessage('');
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [searchLoading]);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchError('Please enter a search query');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    setAnalysis('');
    setOriginalQuery('');
    setEnhancedQuery('');
    setTotalResults(0);

    try {
      const data = await searchProjects(query, 15);

      setSearchResults(data.results);
      setAnalysis(data.analysis || '');
      setOriginalQuery(data.original_query || '');
      setEnhancedQuery(data.enhanced_query || '');
      setTotalResults(data.total_results || 0);
      
      if (data.results.length === 0) {
        setSearchError('No projects found matching your search query');
      }
    } catch (err) {
      console.error('Search error:', err);
      let errorMessage = 'An error occurred while searching. Please try again.';
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = 'Search request timed out. The AI processing is taking longer than expected. Please try again.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Search endpoint not found. Please check if the API is running correctly.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again in a few moments.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      }
      
      setSearchError(errorMessage);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleStructuredSearch = async (searchData) => {
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    setAnalysis('');
    setOriginalQuery('');
    setEnhancedQuery('');
    setTotalResults(0);

    try {
      const data = await searchProjectsStructured(searchData);

      setSearchResults(data.results);
      setAnalysis(data.analysis || '');
      setOriginalQuery(data.original_query || '');
      setEnhancedQuery(data.enhanced_query || '');
      setTotalResults(data.total_results || 0);
      
      if (data.results.length === 0) {
        setSearchError('No projects found matching your criteria. Try adjusting your preferences.');
      }
    } catch (err) {
      console.error('Structured search error:', err);
      let errorMessage = 'An error occurred while searching. Please try again.';
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        errorMessage = 'Search request timed out. The AI processing is taking longer than expected. Please try again.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Search endpoint not found. Please check if the API is running correctly.';
      } else if (err.response?.status === 422) {
        // Handle validation errors
        if (Array.isArray(err.response?.data?.detail)) {
          const validationErrors = err.response.data.detail
            .map(error => `${error.loc.join('.')}: ${error.msg}`)
            .join(', ');
          errorMessage = `Validation error: ${validationErrors}`;
        } else if (err.response?.data?.detail) {
          errorMessage = `Validation error: ${err.response.data.detail}`;
        } else {
          errorMessage = 'Invalid request data. Please check your inputs.';
        }
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again in a few moments.';
      } else if (err.response?.data?.detail) {
        // Handle other error details safely
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else {
          errorMessage = 'An error occurred with the request.';
        }
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      }
      
      setSearchError(errorMessage);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="search-page">
      {/* Search Mode Toggle */}
      <div className="search-mode-toggle">
        <button 
          className={`mode-button ${searchMode === 'structured' ? 'active' : ''}`}
          onClick={() => setSearchMode('structured')}
          disabled={searchLoading}
        >
          🎯 Enhanced Search
        </button>
        <button 
          className={`mode-button ${searchMode === 'text' ? 'active' : ''}`}
          onClick={() => setSearchMode('text')}
          disabled={searchLoading}
        >
          🔍 Quick Search
        </button>
      </div>

      {/* Conditional Search Interface */}
      {searchMode === 'structured' ? (
        <SearchForm onSearch={handleStructuredSearch} loading={searchLoading} />
      ) : (
        <SearchBox onSearch={handleSearch} loading={searchLoading} />
      )}

      {searchLoading && (
        <LoadingSpinner 
          message={`${loadingMessage} (${loadingTime}s)`}
          showProgress={loadingTime > 10}
          progress={Math.min((loadingTime / 90) * 100, 100)}
        />
      )}

      {searchError && (
        <div className="error">
          <strong>Search Failed:</strong> {searchError}
        </div>
      )}

      {(searchResults.length > 0 || analysis) && !searchLoading && (
        <div className="results-section">
          {/* Debug Section - Collapsible */}
          {(originalQuery || enhancedQuery) && (
            <div className="debug-section">
              <button 
                className="debug-toggle"
                onClick={() => setShowDebug(!showDebug)}
              >
                � Debug Info {showDebug ? '▼' : '▶'}
              </button>
              
              {showDebug && (
                <div className="debug-content">
                  <div className="debug-item">
                    <strong>Original Query:</strong>
                    <code>"{originalQuery}"</code>
                  </div>
                  <div className="debug-item">
                    <strong>AI Enhanced Query:</strong>
                    <code>"{enhancedQuery}"</code>
                  </div>
                  <div className="debug-item">
                    <strong>Results Found:</strong>
                    <span>{totalResults} projects</span>
                  </div>
                  <div className="debug-item">
                    <strong>Search Mode:</strong>
                    <span>{searchMode === 'structured' ? 'Enhanced Search' : 'Quick Search'}</span>
                  </div>
                  <div className="debug-item">
                    <strong>Search Limit:</strong>
                    <span>Maximum 15 results</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Main AI Chat Response */}
          {analysis && (
            <div className="ai-chat-response">
              <div className="chat-header">
                <h2>🏠 Real Estate Assistant</h2>
                <span className="chat-subtitle">AI-powered recommendations for you</span>
              </div>
              <div className="chat-content">
                {analysis.split('\n').map((paragraph, index) => (
                  paragraph.trim() && <p key={index} dangerouslySetInnerHTML={{
                    __html: paragraph
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  }} />
                ))}
              </div>
            </div>
          )}
          
          {/* Detailed Results Section */}
          {searchResults.length > 0 && (
            <div className="detailed-results">
              <div className="results-header">
                <h3>📋 Detailed Project Information</h3>
                <span className="results-count">
                  Showing {searchResults.length} project{searchResults.length !== 1 ? 's' : ''}
                </span>
              </div>
              <SearchResults results={searchResults} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchPage;
